import {
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  Vendor,
  VendorContactPerson,
  VendorDetail,
} from 'src/app/shared/models/database-models';
import { Subject, lastValueFrom, takeUntil } from 'rxjs';
import {
  DropDownOptions,
  MyPurchaseForm,
} from 'src/app/shared/models/app-models';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Params } from '@angular/router';
import { BackendApiService } from 'src/app/core/services/backend-api';
import { Store } from '@ngrx/store';
import { updateVendorsToMyPurchase } from '../../../../../core/store/form.actions';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface EditVendorDialogData {
  VendorNumber: string;
  vendorEdited: Vendor;
  routeParams: Params;
}

export interface VendorDowndownOptions {
  id: string | null;
  value: string;
}
@Component({
  selector: 'app-edit-vendor-dialog',
  templateUrl: './edit-vendor-dialog.component.html',
  styleUrls: ['./edit-vendor-dialog.component.scss'],
})
export class EditVendorDialogComponent implements OnInit, OnDestroy {
  destroy$: Subject<boolean> = new Subject<boolean>();
  EmailOptionControl!: FormControl;
  OtherEmailInputControl!:FormControl;
  JustificationControl!: FormControl;
  emailOptions: VendorDowndownOptions[] = [];
  vendorEditForm!: FormGroup;
  VendorNumber!: string;
  vendorEdited!: Vendor;
  mpNumber: string = '';
  vendorDetail: VendorDetail = {
    Name: '',
    VendorNumber: '',
    AddressCountryCode: '',
    EmailAddress: '',
    VendorStatus: '',
    Justification: '',
    VendorContactPerson: [],
    SelectedContactPersonID: null,
    OtherPersonName: null,
    OtherPersonEmail: null,
  };
  purchaseType: string = '';
  vendorForm!: Vendor[];
  constructor(
    private _fb: FormBuilder,
    private BackendApi: BackendApiService,
    private store: Store<{ form: MyPurchaseForm }>,
    public dialogRef: MatDialogRef<EditVendorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditVendorDialogData
  ) {
    this.VendorNumber = data.VendorNumber;
    this.vendorEdited = {...data.vendorEdited};

    this.purchaseType = data.routeParams['purchaseType'].split(
      ';'
    )[0] as string;
    this.mpNumber = data.routeParams['id'];
    this.store
      .select('form')
      .pipe(takeUntil(this.destroy$))
      .subscribe((form: MyPurchaseForm) => {
        this.vendorForm = form.VendorsToSource;
      });
    this.vendorEditForm = this._fb.group({
      rfqSelectedEmail: '',
      name: '',
      no: '',
      status: '',
      conntry: '',
      email: '',
      ...((this.purchaseType === 'tender' || this.purchaseType === 'dc') ? { Justification: '' } : {}),
    });
    this.EmailOptionControl = this.vendorEditForm.controls[
      'rfqSelectedEmail'
    ] as FormControl;
    this.OtherEmailInputControl = this.vendorEditForm.controls[
      'email'
    ] as FormControl;
    this.OtherEmailInputControl.addValidators(Validators.email)
    this.OtherEmailInputControl.updateValueAndValidity()
    this.JustificationControl = this.vendorEditForm.controls[
      'Justification'
    ] as FormControl;
  }

  async ngOnInit(): Promise<void> {
    try {
      await this.getVendorDetail();
    } catch (e: any) {
      console.log('error retrieving data ', e);
    }

    this.JustificationControl?.valueChanges.subscribe((text: string) => {
      this.vendorDetail.Justification = text;
    });
  }

  async editVendor() {
    const EmailSelectedId = this.vendorEditForm.value.rfqSelectedEmail;
    const EmailSelected = this.vendorDetail.VendorContactPerson?.find(
      (item) => +item.VendorContactPersonID === +EmailSelectedId
    );
    let checkedValue;
    if(EmailSelectedId === 'others'){
      checkedValue = 0 
    }else if(EmailSelectedId === null){
      checkedValue = null
    }else{
      checkedValue = +EmailSelectedId
    }
    let dataEdit: Vendor = {
      ...this.vendorEdited,
      EmailAddress: EmailSelected?.EmailAddress || null,
      VendorContactPerson: EmailSelected?.Name || '',
      OtherPersonEmail: null,
      OtherPersonName: null,
      ...((this.purchaseType === 'tender' || this.purchaseType === 'dc')
        ? { Justification: this.vendorEditForm.value.Justification }
        : {}),
      ContactPersonID: checkedValue,
    };
    if (EmailSelectedId === 'others') {
      dataEdit = {
        ...dataEdit,
        EmailAddress: this.vendorDetail.EmailAddress,
        OtherPersonEmail: this.vendorEditForm.value.email,
        OtherPersonName: this.vendorEditForm.value.name,
      };
    }
    const newVendors = this.vendorForm?.map(({VendorContactPersonFullName, ...item}) => {
      if (item.VendorNumber === dataEdit.VendorNumber && !item.isDeleted && !item.Strikethrough) {
        return dataEdit;
      } else {
        return item;
      }
    });
    this.store.dispatch(updateVendorsToMyPurchase({ vendors: newVendors }));
    this.dialogRef.close();
  }

  async getVendorDetail() {
    try {
      this.vendorDetail = await lastValueFrom(
        this.BackendApi.getVendorDetail(this.VendorNumber)
      );
      this.renderPersonAndEmail();
      this.setVendorForm();
    } catch {
      throw Error('Backend problem');
    }
  }

  setVendorForm() {
    this.vendorEditForm.reset();
    const { ContactPersonID, OtherPersonName, OtherPersonEmail } =
      this.vendorEdited;

    const selectedPersonId = ContactPersonID;
    this.vendorEditForm.setValue({
      rfqSelectedEmail: selectedPersonId === 0 ? 'others' : selectedPersonId,
      no: this.vendorDetail.VendorNumber || '',
      status: this.vendorEdited?.VendorStatus || '',
      conntry: this.vendorEdited?.AddressCountryCode,
      name: selectedPersonId ? '' : OtherPersonName || '',
      email: selectedPersonId ? '' : OtherPersonEmail || '',
      ...((this.purchaseType === 'tender' || this.purchaseType === 'dc')
        ? {
            Justification:
              this.vendorEdited?.Justification ||
              this.vendorDetail?.Justification ||
              '',
          }
        : {}),
    });
    if (selectedPersonId) {
      const selectedVendorId = selectedPersonId === 0 ? 'others' : selectedPersonId.toString();
      const matchingOption = this.emailOptions.find(option => option.id === selectedVendorId);
      if (matchingOption) {
        this.EmailOptionControl.setValue(matchingOption.id);
      }
    }
    
   }
  

  renderPersonAndEmail() {
    this.emailOptions = [
      ...this.vendorDetail.VendorContactPerson.map(
        (item: VendorContactPerson) => {
          const { Name, EmailAddress, VendorContactPersonID } = item;
          return {
            id: VendorContactPersonID ? VendorContactPersonID+ '': null,
            value: `${Name ? Name : ''}${Name && EmailAddress ? ',' : ''} ${
              EmailAddress ? EmailAddress : ''
            }`,
          };
        }
      ),
      { id: 'others', value: 'Others, please specify' },
    ];
    console.log("neeraj this.emailOptions",this.emailOptions)

  }

  justificationChange(e: any) {
    this.JustificationControl.setValue(e.target.value);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
